import type {
  EnhancedNavItemBlueprint,
  IPenThemes,
  LegacyExtensionDataRefToValue,
} from '@ipen-portal/plugin-common-react';
import type { FC, ReactElement, ReactNode } from 'react';

import {
  IpenThemeProvider,
  MobileViewContext,
  useGetEntityByRef,
} from '@ipen-portal/plugin-common-react';
import { useContext, useMemo } from 'react';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';

import { BreadcrumbContext } from '../../context';
import { DesktopNavigation } from './components/DesktopNavigation/DesktopNavigation';
import { MobileNavigation } from './components/MobileNavigation';
import { useResolveNavigation } from './hooks/useResolveNavigation';
import { findActiveNavItem } from './utils/findActiveNavItem';

type Props = {
  items: (LegacyExtensionDataRefToValue<
      typeof EnhancedNavItemBlueprint.dataRefs.target
  >['value'] & {
    featureFlag?: string;
    id: string;
  })[];
  mobileHeaderAdditions: ReactNode;
  version: ReactElement;
};

export const Navigation: FC<Props> = ({
  items,
  mobileHeaderAdditions,
  version,
}) => {
  const { isMobileView } = useContext(MobileViewContext);
  const { navigation } = useResolveNavigation(items);
  const currentLocation = useLocation();

  const { breadcrumbs } = useContext(BreadcrumbContext);

  const { kind, name, namespace } =
    matchPath(
      { end: false, path: '/catalog/:namespace/:kind/:name/*' },
      currentLocation.pathname,
    )?.params ?? {};
  const { value: entity } = useGetEntityByRef(
    kind && namespace && name ? { kind, name, namespace } : null,
  );

  const activeNavItem = useMemo(
    () =>
      findActiveNavItem(currentLocation, breadcrumbs ?? [], entity, navigation),
    [currentLocation, navigation, entity, breadcrumbs],
  );

  const selectedThemeId: IPenThemes = activeNavItem?.pageTheme ?? 'default';

  return (
    <IpenThemeProvider themeId={selectedThemeId}>
      {isMobileView
        ? (
            <MobileNavigation
              activeNavItem={activeNavItem}
              headerAdditions={mobileHeaderAdditions}
              navItems={navigation}
              version={version}
            />
          )
        : (
            <DesktopNavigation
              activeNavItem={activeNavItem}
              navItems={navigation}
              version={version}
            />
          )}
    </IpenThemeProvider>
  );
};
